import { useEffect, useState, useCallback } from "react";
import api, { fmtIDR, fmtDate } from "@/lib/api";
import { PageHeader, Loading, Empty, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const NEXT = { antre: "diproses", diproses: "selesai", selesai: "diambil" };

const ACTIVE_BRANCH = () => localStorage.getItem("branch_id") || "all";

export default function Services() {
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [spareparts, setSpareparts] = useState([]);
  const [parts, setParts] = useState([]);
  const [partSel, setPartSel] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [techFilter, setTechFilter] = useState("all");

  const shown = rows.filter((r) => {
    if (techFilter !== "all" && r.technician_id !== techFilter) return false;
    if (q) {
      const hay = `${r.imei || ""} ${r.device_name || ""} ${r.customer_name || ""} ${r.customer_phone || ""} ${r.invoice || ""}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/services", { params: filter ? { status: filter } : {} })
      .then(({ data }) => setRows(data)).finally(() => setLoading(false));
  }, [filter]);
  useEffect(() => { load(); api.get("/staff").then(({ data }) => setStaff(data)).catch(() => {}); api.get("/spareparts").then(({ data }) => setSpareparts(data)).catch(() => {}); }, [load]);

  const addPart = () => {
    const sp = spareparts.find((s) => s.id === partSel);
    if (!sp) return;
    if (parts.find((p) => p.sparepart_id === sp.id)) return toast.error("Sparepart sudah dipilih");
    setParts([...parts, { sparepart_id: sp.id, name: sp.name, qty: 1, cost_price: sp.cost_price || 0, sell_price: sp.sell_price || 0 }]);
    setPartSel("");
  };
  const setPartQty = (id, qty) => setParts(parts.map((p) => p.sparepart_id === id ? { ...p, qty: Math.max(1, Number(qty)) } : p));
  const removePart = (id) => setParts(parts.filter((p) => p.sparepart_id !== id));
  const partsSell = parts.reduce((s, p) => s + p.qty * p.sell_price, 0);

  const create = async () => {
    if (!form.customer_name || !form.device_name || !form.complaint) return toast.error("Lengkapi data pelanggan, perangkat & keluhan");
    try {
      const tech = staff.find((s) => s.id === form.technician_id);
      await api.post("/services", {
        ...form,
        technician_name: tech?.name || "",
        service_price: Number(form.service_price || 0),
        sparepart_cost: Number(form.sparepart_cost || 0),
        dp: Number(form.dp || 0),
        spareparts_used: parts,
      });
      toast.success("Service tersimpan"); setOpen(false); setForm({}); setParts([]); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const advance = async (row) => {
    const next = NEXT[row.status];
    if (!next) return;
    await api.put(`/services/${row.id}/status`, { status: next, note: "" });
    toast.success(`Status → ${next}`); load();
  };

  const cancelService = async (row) => {
    if (row.status === "batal") return;
    if (!window.confirm(`Batalkan transaksi service ${row.invoice}? Sparepart akan dikembalikan ke stok.`)) return;
    const reason = window.prompt("Alasan pembatalan (opsional):", "") || "";
    try { await api.put(`/services/${row.id}/cancel`, { reason }); toast.success("Service dibatalkan"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal membatalkan"); }
  };

  const ab = ACTIVE_BRANCH();
  const myStaff = staff.filter((s) => ab === "all" || !ab ? true : s.branch_id === ab);

  return (
    <div>
      <PageHeader title="Data Service" subtitle="Kelola order service / reparasi">
        <Button onClick={() => { setForm({ technician_id: "" }); setParts([]); setOpen(true); }} className="bg-sky-600 hover:bg-sky-700" data-testid="service-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Service Baru
        </Button>
      </PageHeader>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {["", "antre", "diproses", "selesai", "diambil"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-md text-sm capitalize ${filter === s ? "bg-sky-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
            {s || "Semua"}
          </button>
        ))}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari IMEI / HP / pelanggan / WA..."
          className="ml-auto h-9 px-3 rounded-md border border-slate-200 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-sky-200" data-testid="service-search" />
        <Select value={techFilter} onValueChange={setTechFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="service-tech-filter"><SelectValue placeholder="Teknisi" /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua Teknisi</SelectItem>{myStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Invoice", "Pelanggan", "Perangkat", "Keluhan", "Teknisi", "Total", "Sisa", "Status", "Aksi"].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading ? <tr><td colSpan={9}><Loading /></td></tr>
              : shown.length === 0 ? <tr><td colSpan={9}><Empty /></td></tr>
              : shown.map((r) => (
                <tr key={r.id} className="hover:bg-sky-50 cursor-pointer" onClick={() => setDetail(r)} data-testid={`service-row-${r.id}`}>
                  <td className="px-4 py-3 font-mono-num text-xs">{r.invoice}</td>
                  <td className="px-4 py-3">{r.customer_name}<div className="text-xs text-slate-400">{r.customer_phone}</div></td>
                  <td className="px-4 py-3">{r.device_name}</td>
                  <td className="px-4 py-3 max-w-[200px] truncate text-slate-500">{r.complaint}</td>
                  <td className="px-4 py-3">{r.technician_name || "-"}</td>
                  <td className="px-4 py-3 font-mono-num text-right">{fmtIDR(r.total_price)}</td>
                  <td className="px-4 py-3 font-mono-num text-right text-amber-600">{fmtIDR(r.remaining)}</td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {NEXT[r.status] && (
                      <Button size="sm" variant="outline" onClick={() => advance(r)} data-testid={`advance-${r.id}`} className="text-xs mr-1">
                        → {NEXT[r.status]}
                      </Button>
                    )}
                    {r.status !== "batal" && r.status !== "diambil" && (
                      <Button size="sm" variant="outline" onClick={() => cancelService(r)} data-testid={`service-cancel-${r.id}`} className="text-xs text-red-600 border-red-200">Batalkan</Button>
                    )}
                    {r.status === "batal" && <span className="text-xs text-red-500">Dibatalkan</span>}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Formulir Service</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Nama Pelanggan" v={form.customer_name} on={(v) => setForm({ ...form, customer_name: v })} />
            <Field label="Telepon" v={form.customer_phone} on={(v) => setForm({ ...form, customer_phone: v })} />
            <Field label="Perangkat" v={form.device_name} on={(v) => setForm({ ...form, device_name: v })} />
            <Field label="IMEI" v={form.imei} on={(v) => setForm({ ...form, imei: v })} />
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Keluhan</Label>
              <Textarea value={form.complaint || ""} onChange={(e) => setForm({ ...form, complaint: e.target.value })} className="mt-1" data-testid="service-complaint" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs text-slate-500">Teknisi</Label>
              <Select value={form.technician_id || ""} onValueChange={(v) => setForm({ ...form, technician_id: v })}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Pilih teknisi" /></SelectTrigger>
                <SelectContent>{myStaff.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 bg-sky-50/60 border border-sky-100 rounded-lg p-3">
              <Label className="text-xs text-slate-600 font-medium">Sparepart yang Digunakan (stok berkurang otomatis)</Label>
              <div className="flex gap-2 mt-1.5">
                <Select value={partSel} onValueChange={setPartSel}>
                  <SelectTrigger className="flex-1 bg-white" data-testid="service-part-select"><SelectValue placeholder="Pilih sparepart" /></SelectTrigger>
                  <SelectContent>{spareparts.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} (stok {s.stock})</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={addPart} data-testid="service-part-add">Tambah</Button>
              </div>
              {parts.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {parts.map((p) => (
                    <div key={p.sparepart_id} className="flex items-center gap-2 text-sm bg-white rounded-md px-2 py-1.5 border border-slate-100">
                      <span className="flex-1 truncate">{p.name}</span>
                      <Input type="number" value={p.qty} onChange={(e) => setPartQty(p.sparepart_id, e.target.value)} className="w-16 h-7" />
                      <button type="button" onClick={() => removePart(p.sparepart_id)} className="text-red-500 text-xs px-1">Hapus</button>
                    </div>
                  ))}
                  <div className="text-xs text-slate-500 text-right">Total harga sparepart: <span className="font-mono-num font-medium text-slate-700">{fmtIDR(partsSell)}</span></div>
                </div>
              )}
            </div>
            <Field label="Jenis Service" v={form.service_type} on={(v) => setForm({ ...form, service_type: v })} />
            <Field label="Harga Service" type="number" v={form.service_price} on={(v) => setForm({ ...form, service_price: v })} />
            <Field label="Biaya Sparepart" type="number" v={form.sparepart_cost} on={(v) => setForm({ ...form, sparepart_cost: v })} />
            <Field label="DP" type="number" v={form.dp} on={(v) => setForm({ ...form, dp: v })} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={create} className="bg-sky-600 hover:bg-sky-700" data-testid="service-save-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Detail Servis {detail?.invoice}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-2">
              <div className="flex justify-between"><span className="text-slate-500">Pelanggan</span><span className="font-medium">{detail.customer_name} · {detail.customer_phone}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Perangkat</span><span>{detail.device_name} {detail.imei && `· ${detail.imei}`}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Teknisi</span><span>{detail.technician_name || "-"}</span></div>
              <div><span className="text-slate-500">Keluhan:</span> {detail.complaint}</div>
              {detail.service_type && <div><span className="text-slate-500">Tindakan:</span> {detail.service_type}</div>}
              {(detail.spareparts_used || []).length > 0 && (
                <div><div className="text-slate-500 mb-1">Sparepart:</div>
                  {detail.spareparts_used.map((p, i) => <div key={i} className="flex justify-between pl-2"><span>{p.name} x{p.qty}</span><span className="font-mono-num">{fmtIDR(p.sell_price * p.qty)}</span></div>)}
                </div>
              )}
              <div className="pt-2 border-t border-slate-200 font-mono-num space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Biaya Jasa</span><span>{fmtIDR(detail.service_price)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Sparepart</span><span>{fmtIDR(detail.parts_sell ?? detail.sparepart_cost)}</span></div>
                <div className="flex justify-between text-lg font-semibold"><span>Total</span><span>{fmtIDR(detail.total_price)}</span></div>
                <div className="flex justify-between text-amber-600"><span>Sisa</span><span>{fmtIDR(detail.remaining)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status</span><span className="uppercase">{detail.status}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, v, on, type = "text" }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    <Input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="mt-1" />
  </div>
);
